import { Routes, Route, Navigate } from 'react-router-dom'
import { useEffect, Suspense, lazy } from 'react'
import { AuthProvider } from './contexts/AuthContext'
import { ProtectedRoute } from './components/ProtectedRoute'
import Login from './pages/Login'
import Layout from './components/Layout'

// Lazy load heavy components to reduce initial bundle size
const BoxDashboard = lazy(() => import('./pages/BoxDashboard'))
const BoxDetails = lazy(() => import('./pages/BoxDetails'))
const EbayBoxDetails = lazy(() => import('./pages/EbayBoxDetails'))
const OAuthCallback = lazy(() => import('./pages/OAuthCallback'))
const TCGBoxSearch = lazy(() => import('./components/TCGBoxSearch'))
const InventoryDashboard = lazy(() => import('./pages/InventoryDashboard'))
const OdooProductDetail = lazy(() => import('./pages/OdooProductDetail'))
const TCGOracle = lazy(() => import('./pages/TCGOracle'))
const PokeDataDashboard = lazy(() => import('./pages/PokeDataDashboard'))
// const PremiumCatalog = lazy(() => import('./pages/PremiumCatalog'))
const PremiumLayout = lazy(() => import('./pages/premium/PremiumLayout'))
const PremiumHome = lazy(() => import('./pages/premium/PremiumHome'))
const PremiumSets = lazy(() => import('./pages/premium/PremiumSets'))
const PremiumCards = lazy(() => import('./pages/premium/PremiumCards'))
const PremiumDecks = lazy(() => import('./pages/premium/PremiumDecks'))
const DatabaseExplorer = lazy(() => import('./pages/DatabaseExplorer'))
const ServerExplorer = lazy(() => import('./pages/ServerExplorer'))
const SalesDashboard = lazy(() => import('./pages/SalesDashboard'))
const WebsiteManager = lazy(() => import('./pages/WebsiteManager'))
const VirtualWarehouseDashboard = lazy(() => import('./pages/VirtualWarehouseDashboard'))

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex items-center justify-center h-64 bg-gray-900">
    <div className="text-center">
      <div className="relative">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-gray-700 border-t-blue-500 mx-auto"></div>
        <div className="animate-ping absolute inset-0 rounded-full h-12 w-12 border-2 border-blue-400 opacity-30"></div>
      </div>
      <div className="mt-4 text-gray-300 font-medium">Loading Application...</div>
    </div>
  </div>
)

function App() {
  useEffect(() => {
    // Force dark mode
    document.documentElement.classList.add('dark');
  }, []);

  return (
    <AuthProvider>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/oauth/callback" element={<OAuthCallback />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<LoadingFallback />}>
                    <Routes>
                      <Route path="/" element={<Navigate to="/inventory" replace />} />
                      <Route path="/inventory" element={<InventoryDashboard />} />
                      <Route path="/pokedata" element={<PokeDataDashboard />} />
                      
                      {/* Premium Catalog Suite */}
                      <Route path="/premium-catalog" element={<PremiumLayout />}>
                        <Route index element={<Navigate to="home" replace />} />
                        <Route path="home" element={<PremiumHome />} />
                        <Route path="sets" element={<PremiumSets />} />
                        <Route path="cards" element={<PremiumCards />} />
                        <Route path="decks" element={<PremiumDecks />} />
                      </Route>

                      <Route path="/tcg-oracle" element={<TCGOracle />} />
                      <Route path="/odoo/product/:id" element={<OdooProductDetail />} />
                      <Route path="/tcg-box-search" element={<TCGBoxSearch />} />
                      <Route path="/box/:id" element={<BoxDetails />} />
                      <Route path="/ebay-box/:itemId" element={<EbayBoxDetails />} />
                      <Route path="/box-dashboard" element={<BoxDashboard />} />
                      <Route path="/db" element={<DatabaseExplorer />} />
                      <Route path="/server" element={<ServerExplorer />} />
                      <Route path="/sales" element={<SalesDashboard />} />
                      <Route path="/website" element={<WebsiteManager />} />
                      <Route path="/warehouse" element={<VirtualWarehouseDashboard />} />
                      {/* Temporarily disabled problematic pages with 404 errors */}
                      {/* <Route path="/market-intelligence" element={<MarketIntelligence />} />
                      <Route path="/real-market-intelligence" element={<RealMarketIntelligence />} />
                      <Route path="/research-tool" element={<ResearchTool />} />
                      <Route path="/browse-api" element={<BrowseApiDashboard />} />
                      <Route path="/cards/:id/analytics" element={<CardAnalytics />} />
                      <Route path="/data-collection" element={<DataCollection />} />
                      <Route path="/ebay-monitoring" element={<EbayApiMonitoring />} /> */}
                    </Routes>
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Suspense>
    </AuthProvider>
  )
}

export default App
